/**
 * Trunnion AI - Fuzzy Search Utility
 * Simple, fast fuzzy matching for command palette and global search.
 */

export interface FuzzyResult<T> {
  item: T;
  score: number;
}

/**
 * Score a query against a target string.
 * Returns 0 for no match, higher values for better matches.
 * Prioritizes: exact match > starts with > consecutive chars > scattered chars.
 */
export function fuzzyScore(query: string, target: string): number {
  if (!query) return 1;

  const q = query.toLowerCase();
  const t = target.toLowerCase();

  // Exact match
  if (t === q) return 100;

  // Starts with
  if (t.startsWith(q)) return 90;

  // Contains as substring
  const substringIndex = t.indexOf(q);
  if (substringIndex !== -1) return 80 - substringIndex * 0.5;

  // Character-by-character fuzzy match
  let qi = 0;
  let score = 0;
  let lastMatchIndex = -1;
  let consecutiveBonus = 0;

  for (let ti = 0; ti < t.length && qi < q.length; ti++) {
    if (t[ti] === q[qi]) {
      score += 10;

      // Bonus for consecutive matches
      if (lastMatchIndex === ti - 1) {
        consecutiveBonus += 5;
        score += consecutiveBonus;
      } else {
        consecutiveBonus = 0;
      }

      // Bonus for matching at word boundaries
      if (ti === 0 || t[ti - 1] === ' ' || t[ti - 1] === '-' || t[ti - 1] === '_') {
        score += 10;
      }

      lastMatchIndex = ti;
      qi++;
    }
  }

  // All query chars must match
  if (qi < q.length) return 0;

  // Penalize long targets (prefer shorter, more relevant results)
  score -= (t.length - q.length) * 0.5;

  return Math.max(score, 1);
}

/**
 * Filter and rank items by fuzzy match against a text field.
 */
export function fuzzySearch<T>(
  items: T[],
  query: string,
  getText: (item: T) => string,
  minScore = 1,
): FuzzyResult<T>[] {
  if (!query.trim()) return items.map((item) => ({ item, score: 1 }));

  return items
    .map((item) => ({
      item,
      score: fuzzyScore(query, getText(item)),
    }))
    .filter((r) => r.score >= minScore)
    .sort((a, b) => b.score - a.score);
}
